import type { CSSProperties } from "react"
import { ArrowUpRight, Pause } from "lucide-react"
import { Separator } from "@workspace/ui/components/separator"
import { Badge } from "@workspace/ui/components/badge"
import { cn } from "@workspace/ui/lib/utils"
import { useI18n } from "@/lib/i18n"
import { ImageSlot } from "./image-slot"

const delay = (n: number) => ({ "--d": n } as CSSProperties)

export function Work() {
  const { t } = useI18n()

  // Duplicate cases so the marquee track loops seamlessly.
  const loop = [...t.work.cases, ...t.work.cases]

  return (
    <section id="work" className="relative border-t border-border py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-14 xl:px-20">
        {/* Numbered heading */}
        <header className="rise-stagger flex items-center gap-5">
          <h2
            style={delay(0)}
            className="flex items-baseline gap-3 text-2xl font-semibold tracking-[-0.02em] md:text-3xl"
          >
            <span className="font-mono text-sm font-normal text-primary">01.</span>
            <span className="text-foreground">{t.work.eyebrow}</span>
          </h2>
          <Separator style={delay(1)} className="h-px flex-1 bg-foreground/20" />
          <span
            style={delay(2)}
            className="eyebrow-sm hidden items-center gap-1.5 text-foreground/70 md:inline-flex"
          >
            <Pause className="size-3" strokeWidth={1.6} />
            hover to pause
          </span>
        </header>

        {/* Title + lead */}
        <div className="rise-stagger mt-10 grid grid-cols-12 gap-x-6 gap-y-4">
          <h3
            style={delay(3)}
            className="col-span-12 text-[clamp(1.6rem,3.4vw,2.6rem)] leading-[1.08] font-extrabold tracking-[-0.03em] text-foreground md:col-span-8"
          >
            {t.work.title}
          </h3>
          <p
            style={delay(4)}
            className="col-span-12 max-w-2xl text-[14.5px] leading-[1.65] text-foreground/65 md:col-span-10"
          >
            {t.work.lead}
          </p>
        </div>
      </div>

      {/* Full-bleed marquee — edge fades mask the ends for the infinite feel */}
      <div
        className="marquee mt-12 md:mt-16"
        style={
          {
            "--marquee-gap": "1.5rem",
            "--marquee-duration": "58s",
          } as CSSProperties
        }
      >
        <div className="marquee-track">
          {loop.map((c, i) => {
            const isDupe = i >= t.work.cases.length
            const card = (
              <article
                key={`${c.client}-${i}`}
                className={cn(
                  "group flex h-full w-[320px] shrink-0 flex-col bg-card border border-border transition-colors duration-300 md:w-[360px] lg:w-[400px] hover:border-primary/50",
                )}
                aria-hidden={isDupe}
              >
                <ImageSlot
                  ratio="4 / 3"
                  tone={c.tone}
                  caption={`${c.client} · ${c.year}`}
                  className="border-0 border-b border-border"
                />
                <div className="flex flex-1 flex-col gap-4 p-5">
                  {/* Meta */}
                  <div className="eyebrow-sm flex items-center gap-2.5 text-foreground/70">
                    <span className="text-foreground">{c.client}</span>
                    <Separator orientation="vertical" className="h-3" />
                    <span>{c.year}</span>
                  </div>
                  {/* Title */}
                  <h4 className="line-clamp-2 text-[1.05rem] leading-[1.25] font-bold tracking-[-0.02em] text-foreground transition-colors duration-300 group-hover:text-primary md:text-[1.1rem]">
                    {c.title}
                  </h4>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {c.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  {/* Footer — view case (or locked) */}
                  <div className="eyebrow-sm mt-auto flex items-center justify-between border-t border-border pt-3">
                    {c.href ? (
                      <span className="text-primary">view case</span>
                    ) : (
                      <span className="text-foreground/40">under NDA</span>
                    )}
                    <ArrowUpRight
                      size={13}
                      className={cn(
                        "transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
                        c.href ? "text-primary" : "text-foreground/40",
                      )}
                    />
                  </div>
                </div>
              </article>
            )
            if (c.href && !isDupe) {
              return (
                <a
                  key={`${c.client}-${i}`}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="contents"
                  aria-label={`${c.client} — ${c.title}`}
                >
                  {card}
                </a>
              )
            }
            return card
          })}
        </div>
      </div>
    </section>
  )
}
